import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth, API } from "../App";
import { APP_VERSION } from "../version";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { Textarea } from "../components/ui/textarea";
import DnsHostChecker from "../components/tools/DnsHostChecker";
import DnsCheckerV2 from "../components/tools/DnsCheckerV2";
import DnsCheckerV3 from "../components/tools/DnsCheckerV3";
import DnsCheckerV4 from "../components/tools/DnsCheckerV4";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  LogOut, Wrench, Clock, CalendarDays, Activity, Play,
  FileVideo, Code, Map, Target, Flame, AlertTriangle,
  Copy, Check, ArrowRight, X, Search, Server, Globe,
  BarChart3, Tv, Film, PlayCircle, User, FileText, Download, Loader2
} from "lucide-react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";

// Tool icons mapping
const TOOL_ICONS = {
  "file-video": FileVideo,
  "code": Code,
  "map": Map,
  "target": Target,
  "flame": Flame,
  "wrench": Wrench,
  "bar-chart": BarChart3,
  "search": Search
};

const getToolIcon = (iconName) => {
  return TOOL_ICONS[iconName] || Wrench;
};

// M3u to Xtream Tool Component
const M3uToXtreamTool = ({ isOpen, onClose, token, onUsageUpdate }) => {
  const [m3uUrl, setM3uUrl] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState("");

  const handleConvert = async () => {
    if (!m3uUrl.trim()) {
      setError("Veuillez coller un lien M3U");
      return;
    }

    setLoading(true);
    setError("");
    setResult(null);

    try {
      const response = await axios.post(
        `${API}/user/tools/m3u-to-xtream`,
        { m3u_url: m3uUrl },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setResult({
          username: response.data.username,
          password: response.data.password,
          url: response.data.url
        });
        onUsageUpdate();
      } else {
        setError(response.data.error || "Erreur de conversion");
      }
    } catch (err) {
      const message = err.response?.data?.detail || "Erreur lors de la conversion";
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = () => {
    if (result) {
      const text = `Username:${result.username}\nPassword:${result.password}\nUrl:${result.url}`;
      navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success("Copié dans le presse-papier");
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleClose = () => {
    setM3uUrl("");
    setResult(null);
    setError("");
    onClose();
  };

  const exampleFormat = "http://domain.com/get.php?username=XXX&password=YYY&type=m3u_plus&output=ts";

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl" data-testid="m3u-xtream-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileVideo className="h-5 w-5 text-primary" />
            M3u → Xstream Converter
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Input Section */}
          <div className="space-y-2">
            <Label>Coller le lien M3U</Label>
            <Textarea
              value={m3uUrl}
              onChange={(e) => {
                setM3uUrl(e.target.value);
                setError("");
              }}
              placeholder={exampleFormat}
              className="font-mono text-sm h-24 bg-input border-transparent focus:border-primary"
              data-testid="m3u-input"
            />
            <p className="text-xs text-muted-foreground">
              Format attendu: <code className="text-accent">{exampleFormat}</code>
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="p-3 bg-destructive/10 border border-destructive/30 rounded-sm text-destructive text-sm flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 flex-shrink-0" />
              {error}
            </div>
          )}

          {/* Convert Button */}
          <Button
            onClick={handleConvert}
            disabled={loading || !m3uUrl.trim()}
            className="w-full bg-primary text-primary-foreground"
            data-testid="convert-button"
          >
            {loading ? (
              "Conversion..."
            ) : (
              <>
                Convertir <ArrowRight className="h-4 w-4 ml-2" />
              </>
            )}
          </Button>

          {/* Result Section */}
          {result && (
            <div className="space-y-3 animate-fade-in" data-testid="conversion-result">
              <div className="flex items-center justify-between">
                <Label className="text-primary">Résultat</Label>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCopy}
                  className="gap-2"
                  data-testid="copy-button"
                >
                  {copied ? (
                    <>
                      <Check className="h-4 w-4 text-primary" />
                      Copié
                    </>
                  ) : (
                    <>
                      <Copy className="h-4 w-4" />
                      Copier
                    </>
                  )}
                </Button>
              </div>
              
              <div className="bg-muted/30 border border-border rounded-sm p-4 font-mono text-sm space-y-2">
                <div className="flex items-center gap-2">
                  <span className="text-muted-foreground w-24">Username:</span>
                  <span className="text-primary font-semibold">{result.username}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-muted-foreground w-24">Password:</span>
                  <span className="text-accent font-semibold">{result.password}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-muted-foreground w-24">Url:</span>
                  <span className="text-foreground">{result.url}</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Country flags shown next to each host (DNS daily check results)
const COUNTRY_FLAGS = {
  ES: "\u{1F1EA}\u{1F1F8}", FR: "\u{1F1EB}\u{1F1F7}", IT: "\u{1F1EE}\u{1F1F9}",
  DE: "\u{1F1E9}\u{1F1EA}", CA: "\u{1F1E8}\u{1F1E6}", US: "\u{1F1FA}\u{1F1F8}",
  MA: "\u{1F1F2}\u{1F1E6}",
};
const COUNTRY_LABELS = {
  ES: "Spain", FR: "France", IT: "Italy", DE: "Germany",
  CA: "Canada", US: "USA", MA: "Morocco",
};
const COUNTRY_ORDER = ["ES", "FR", "IT", "DE", "CA", "US", "MA"];

const HostDnsFlags = ({ dnsCheck }) => {
  // No data yet (daily cron not run) -> show neutral grey state
  if (!dnsCheck || !dnsCheck.countries) {
    return (
      <div className="flex items-center gap-1 flex-shrink-0" title="Verification DNS en attente" data-testid="host-flags-pending">
        <span className="text-[10px] text-muted-foreground/60 px-1.5 py-0.5 rounded bg-muted/30">--</span>
      </div>
    );
  }
  const allDown = dnsCheck.success_count === 0;
  return (
    <div
      className={`flex items-center gap-0.5 flex-shrink-0 ${allDown ? "px-1.5 py-0.5 rounded bg-red-500/10 border border-red-500/30" : ""}`}
      title={`${dnsCheck.success_count}/${dnsCheck.total_count} pays OK`}
      data-testid="host-flags"
    >
      {COUNTRY_ORDER.map((code) => {
        const ok = dnsCheck.countries[code];
        // Only show flags for OK countries; if everything is down, show all in red
        if (!allDown && !ok) return null;
        return (
          <span
            key={code}
            className={`text-base leading-none ${allDown ? "opacity-60 grayscale" : ""}`}
            title={`${COUNTRY_LABELS[code]} ${ok ? "OK" : "KO"}`}
          >
            {COUNTRY_FLAGS[code]}
          </span>
        );
      })}
      {allDown && <X className="h-3 w-3 text-red-400 ml-0.5" />}
    </div>
  );
};

// Find Host Tool Component
const FindHostTool = ({ isOpen, onClose, token, onUsageUpdate }) => {
  const [providers, setProviders] = useState([]);
  const [selectedProvider, setSelectedProvider] = useState("");
  const [hosts, setHosts] = useState([]);
  const [providerName, setProviderName] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingProviders, setLoadingProviders] = useState(true);
  const [revealingIndex, setRevealingIndex] = useState(null);
  const [revealedModal, setRevealedModal] = useState(null); // {host, note, index}
  const [providerSearch, setProviderSearch] = useState("");
  const [showProviderDropdown, setShowProviderDropdown] = useState(false);
  
  // Quota state
  const [globalLimit, setGlobalLimit] = useState(50);
  const [perProviderLimit, setPerProviderLimit] = useState(5);
  const [globalRemaining, setGlobalRemaining] = useState(50);
  const [providerRemaining, setProviderRemaining] = useState(5);
  const [remaining, setRemaining] = useState(50);
  const [cooldownSeconds, setCooldownSeconds] = useState(5);
  const [cooldownActive, setCooldownActive] = useState(false);
  const [cooldownTimer, setCooldownTimer] = useState(0);
  const [providerRevealedCount, setProviderRevealedCount] = useState(0);
  const [globalRevealedCount, setGlobalRevealedCount] = useState(0);
  const [loadingProgress, setLoadingProgress] = useState(null); // {current, total}

  useEffect(() => {
    if (isOpen) {
      fetchProviders();
    }
  }, [isOpen]);

  // Cooldown timer
  useEffect(() => {
    let interval;
    if (cooldownActive && cooldownTimer > 0) {
      interval = setInterval(() => {
        setCooldownTimer(prev => {
          if (prev <= 1) {
            setCooldownActive(false);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [cooldownActive, cooldownTimer]);

  const fetchProviders = async () => {
    setLoadingProviders(true);
    try {
      const response = await axios.get(`${API}/user/providers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProviders(response.data);
    } catch (error) {
      toast.error("Erreur lors du chargement des fournisseurs");
    } finally {
      setLoadingProviders(false);
    }
  };

  const handleProviderSelect = async (providerId) => {
    setSelectedProvider(providerId);
    setHosts([]);
    setProviderName("");
    
    if (!providerId) return;
    
    setLoading(true);
    setHosts([]);
    setLoadingProgress({ current: 0, total: 0 });

    // Cosmetic progress while waiting for the API
    let progTimer = null;
    try {
      const response = await axios.get(`${API}/user/providers/${providerId}/hosts-masked`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = response.data;
      const total = (data.hosts || []).length;
      // Animate counter 1..total quickly (visible "Patientez 1/N")
      setLoadingProgress({ current: 1, total });
      if (total > 1) {
        const step = Math.max(20, Math.floor(600 / total)); // ~600ms total animation
        let i = 1;
        await new Promise((resolve) => {
          progTimer = setInterval(() => {
            i += 1;
            setLoadingProgress({ current: i, total });
            if (i >= total) {
              clearInterval(progTimer);
              resolve();
            }
          }, step);
        });
      }
      setHosts(data.hosts || []);
      setProviderName(data.provider_name || "");
      setGlobalLimit(data.max_display_per_day || 50);
      setPerProviderLimit(data.per_provider_limit || 5);
      setGlobalRemaining(data.global_remaining ?? 50);
      setProviderRemaining(data.provider_remaining ?? 5);
      setRemaining(data.remaining_reveals ?? 0);
      setProviderRevealedCount(data.provider_revealed_count || 0);
      setGlobalRevealedCount(data.revealed_count || 0);
      setCooldownSeconds(data.cooldown_seconds || 5);
    } catch (error) {
      const message = error.response?.data?.detail || "Erreur lors de la récupération des hosts";
      toast.error(message);
    } finally {
      if (progTimer) clearInterval(progTimer);
      setLoadingProgress(null);
      setLoading(false);
    }
  };

  const handleReveal = async (index) => {
    if (revealingIndex !== null || cooldownActive) return;
    
    const host = hosts[index];
    // If clicking on already revealed host, just reopen the big modal
    if (host.is_revealed) {
      setRevealedModal({ host: host.host_masked, note: host.note_masked || "", index });
      return;
    }
    
    setRevealingIndex(index);
    try {
      const response = await axios.post(
        `${API}/user/providers/${selectedProvider}/hosts/reveal`,
        { index },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const data = response.data;
      
      if (data.success) {
        // Only 1 revealed at a time: re-mask all others, reveal this one
        setHosts(prev => prev.map((h, i) => {
          if (i === index) {
            return { ...h, host_masked: data.host, note_masked: data.note || "", is_revealed: true };
          }
          // Re-mask any previously revealed host
          if (h.is_revealed) {
            return { ...h, host_masked: mask_local(h.host_masked), note_masked: h.note_masked ? mask_local(h.note_masked) : "", is_revealed: false };
          }
          return h;
        }));
        setRemaining(data.remaining_reveals ?? remaining - 1);
        if (data.global_remaining !== undefined) setGlobalRemaining(data.global_remaining);
        if (data.provider_remaining !== undefined) setProviderRemaining(data.provider_remaining);
        setProviderRevealedCount(prev => prev + 1);
        setGlobalRevealedCount(prev => prev + 1);

        // Open the large reveal modal so the host is clearly visible on any screen
        setRevealedModal({ host: data.host, note: data.note || "", index });
        
        // Start cooldown
        if (cooldownSeconds > 0) {
          setCooldownActive(true);
          setCooldownTimer(cooldownSeconds);
        }
        
        onUsageUpdate();
      } else {
        if (data.cooldown_remaining) {
          setCooldownActive(true);
          setCooldownTimer(data.cooldown_remaining);
        }
        toast.error(data.error || "Erreur lors de la révélation");
      }
    } catch (error) {
      const message = error.response?.data?.detail || "Erreur lors de la révélation";
      toast.error(message);
    } finally {
      setRevealingIndex(null);
    }
  };

  // Helper to re-mask a string locally (show first 2 chars + asterisks)
  const mask_local = (str) => {
    if (!str || str.length <= 2) return "***";
    return str.substring(0, 2) + "*".repeat(Math.min(str.length - 2, 10));
  };

  const getRevealButtonState = (index) => {
    const host = hosts[index];
    if (host.is_revealed) return { disabled: false, text: "Afficher", variant: "secondary" };
    if (remaining <= 0) return { disabled: true, text: "Limite", variant: "destructive" };
    if (cooldownActive) return { disabled: true, text: `${cooldownTimer}s`, variant: "outline" };
    return { disabled: false, text: "Révéler", variant: "default" };
  };

  const handleClose = () => {
    setSelectedProvider("");
    setHosts([]);
    setProviderName("");
    setProviderSearch("");
    setShowProviderDropdown(false);
    setCooldownActive(false);
    setCooldownTimer(0);
    onClose();
  };

  const providerSuggestions = providerSearch.length >= 1
    ? providers.filter((p) => p.name.toLowerCase().includes(providerSearch.toLowerCase())).slice(0, 15)
    : [];

  const handleProviderSearchChange = (e) => {
    setProviderSearch(e.target.value);
    setShowProviderDropdown(true);
    if (selectedProvider) {
      setSelectedProvider("");
      setHosts([]);
      setProviderName("");
    }
  };

  const handleProviderPick = (provider) => {
    setProviderSearch(provider.name);
    setShowProviderDropdown(false);
    handleProviderSelect(provider.id);
  };

  const globalLimitReached = globalRemaining <= 0;
  const providerLimitReached = providerRemaining <= 0;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto flex flex-col" data-testid="find-host-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Search className="h-5 w-5 text-primary" />
            Trouver votre host
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 flex-1 overflow-y-auto flex flex-col">
          {/* Provider Selection - Autocomplete */}
          <div className="space-y-2">
            <Label>Selectionnez votre fournisseur</Label>
            {loadingProviders ? (
              <div className="h-10 bg-muted/30 rounded-sm animate-pulse" />
            ) : (
              <>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Tapez le nom du fournisseur..."
                  value={providerSearch}
                  onChange={handleProviderSearchChange}
                  onFocus={() => setShowProviderDropdown(true)}
                  className="pl-10"
                  autoFocus
                  data-testid="provider-search-input"
                />
              </div>
              {showProviderDropdown && !selectedProvider && providerSuggestions.length > 0 && (
                <div className="max-h-[200px] overflow-y-auto rounded-md border border-border bg-popover shadow-lg" data-testid="provider-suggestions">
                  {providerSuggestions.map((p) => (
                    <button
                      key={p.id}
                      onClick={() => handleProviderPick(p)}
                      className="w-full text-left px-4 py-2.5 text-sm hover:bg-muted/50 transition-colors border-b border-border/30 last:border-0 flex items-center gap-2"
                      data-testid={`provider-option-${p.id}`}
                    >
                      <Server className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                      {p.name}
                    </button>
                  ))}
                </div>
              )}
              {showProviderDropdown && !selectedProvider && providerSearch.length > 0 && providerSuggestions.length === 0 && (
                <div className="rounded-md border border-border bg-popover shadow-lg px-4 py-3 text-sm text-muted-foreground">
                  Aucun fournisseur trouve
                </div>
              )}
              </>
            )}
          </div>

          {/* Loading State with progress counter */}
          {loading && (
            <div className="flex flex-col items-center justify-center py-12 gap-3" data-testid="find-host-loading">
              <Loader2 className="h-8 w-8 text-primary animate-spin" />
              <div className="text-sm text-muted-foreground">Patientez...</div>
              {loadingProgress && loadingProgress.total > 0 && (
                <div className="flex flex-col items-center gap-1">
                  <div className="text-2xl font-bold font-mono tabular-nums text-primary" data-testid="find-host-progress">
                    {loadingProgress.current}/{loadingProgress.total}
                  </div>
                  <div className="w-48 h-1.5 bg-muted rounded-full overflow-hidden">
                    <div
                      className="h-full bg-primary transition-all duration-100"
                      style={{ width: `${(loadingProgress.current / Math.max(1, loadingProgress.total)) * 100}%` }}
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Hosts Result */}
          {!loading && hosts.length > 0 && (
            <div className="space-y-3 flex-1 overflow-hidden flex flex-col" data-testid="hosts-result">
              {/* Quota badges */}
              <div className="flex items-center justify-between flex-wrap gap-2">
                <Label className="text-primary flex items-center gap-2">
                  <Globe className="h-4 w-4" />
                  Hosts de {providerName} ({hosts.length})
                </Label>
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge 
                    variant={providerLimitReached ? "destructive" : "secondary"} 
                    className="font-mono text-xs"
                    data-testid="provider-quota-badge"
                  >
                    Fournisseur: {providerRevealedCount}/{perProviderLimit}
                  </Badge>
                  <Badge 
                    variant={globalLimitReached ? "destructive" : "outline"} 
                    className="font-mono text-xs"
                    data-testid="global-quota-badge"
                  >
                    Global: {globalRevealedCount}/{globalLimit}
                  </Badge>
                  {cooldownActive && (
                    <Badge variant="outline" className="font-mono text-xs animate-pulse" data-testid="cooldown-badge">
                      Attente: {cooldownTimer}s
                    </Badge>
                  )}
                </div>
              </div>

              {/* Limit reached messages */}
              {globalLimitReached && (
                <div className="p-3 bg-destructive/10 border border-destructive/30 rounded-sm flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-destructive flex-shrink-0" />
                  <span className="text-sm text-destructive">
                    Limite globale atteinte ({globalLimit}/jour). Revenez demain.
                  </span>
                </div>
              )}
              {!globalLimitReached && providerLimitReached && (
                <div className="p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-sm flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-yellow-500 flex-shrink-0" />
                  <span className="text-sm text-yellow-400">
                    Limite atteinte pour ce fournisseur ({perProviderLimit}/jour). Essayez un autre fournisseur.
                  </span>
                </div>
              )}
              
              {/* Hosts list */}
              <div className="bg-muted/30 border border-border rounded-sm p-2 flex-1 overflow-y-auto">
                <div className="space-y-2">
                  {hosts.map((host, index) => {
                    const buttonState = getRevealButtonState(index);
                    const isRevealing = revealingIndex === index;
                    
                    return (
                      <div 
                        key={host.id || index} 
                        className={`flex items-center gap-2 sm:gap-3 py-3 px-3 sm:px-4 rounded-sm transition-colors ${
                          host.is_revealed 
                            ? "bg-primary/10 border border-primary/20" 
                            : "bg-background/50 hover:bg-background/80"
                        }`}
                        data-testid={`host-row-${index}`}
                      >
                        {/* Index number */}
                        <span className="text-xs text-muted-foreground font-mono w-6 flex-shrink-0">
                          #{index + 1}
                        </span>
                        
                        {/* Host & Note */}
                        <div className="flex-1 min-w-0">
                          {host.is_revealed ? (
                            <div 
                              className="select-none"
                              onContextMenu={(e) => e.preventDefault()}
                              onDragStart={(e) => e.preventDefault()}
                            >
                              <img
                                src={`${API}/host-image/${encodeURIComponent(host.host_masked)}?token=${token}`}
                                alt="host"
                                className="h-7 sm:h-8 max-w-full object-contain object-left"
                                draggable={false}
                                onContextMenu={(e) => e.preventDefault()}
                                data-testid={`host-image-${index}`}
                                style={{ userSelect: "none", pointerEvents: "none" }}
                              />
                              {host.note_masked && (
                                <span className="ml-0 text-xs text-muted-foreground block mt-1">
                                  {host.note_masked}
                                </span>
                              )}
                            </div>
                          ) : (
                            <div className="flex items-center gap-2">
                              <Globe className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
                              <span className="font-mono text-sm text-muted-foreground">
                                {host.host_masked}
                              </span>
                            </div>
                          )}
                        </div>

                        {/* DNS Status flags (per country) - hidden on mobile when revealed to give space to the host image */}
                        <div className={host.is_revealed ? "hidden sm:flex flex-shrink-0" : "flex flex-shrink-0"}>
                          <HostDnsFlags dnsCheck={host.dns_check} />
                        </div>

                        {/* Action buttons */}
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {host.is_revealed ? (
                            <Badge variant="secondary" className="text-xs">
                              <Check className="h-3 w-3 mr-1" />
                              Affiché
                            </Badge>
                          ) : (
                            <Button
                              variant={buttonState.variant}
                              size="sm"
                              onClick={() => handleReveal(index)}
                              disabled={buttonState.disabled || isRevealing}
                              className="text-xs"
                              data-testid={`reveal-btn-${index}`}
                            >
                              {isRevealing ? "..." : buttonState.text}
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* No providers message */}
          {!loadingProviders && providers.length === 0 && (
            <div className="text-center py-8 text-muted-foreground">
              <Server className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>Aucun fournisseur disponible.</p>
              <p className="text-sm">Contactez l'administrateur.</p>
            </div>
          )}
        </div>
      </DialogContent>

      {/* Large reveal modal: shows the revealed host clearly on any screen size */}
      <Dialog open={!!revealedModal} onOpenChange={(open) => !open && setRevealedModal(null)}>
        <DialogContent
          className="max-w-[95vw] sm:max-w-lg p-0 overflow-hidden border-primary/30"
          data-testid="revealed-host-modal"
        >
          <div className="bg-gradient-to-br from-emerald-950 via-zinc-950 to-black p-6 sm:p-8">
            <div className="flex flex-col items-center gap-5">
              <div className="flex items-center gap-2 text-emerald-400">
                <Globe className="h-5 w-5" />
                <span className="text-sm font-medium uppercase tracking-wider">Host #{(revealedModal?.index ?? 0) + 1}</span>
              </div>

              {/* The host image - rendered server-side, no copy possible */}
              <div
                className="w-full rounded-xl bg-black/60 border border-emerald-500/40 p-4 flex items-center justify-center min-h-[80px]"
                style={{
                  fontFamily: "monospace",
                  wordBreak: "break-all",
                  overflowWrap: "anywhere",
                  textAlign: "center",
                }}
              >
                {revealedModal && (
                  <img
                    src={`${API}/host-image/${encodeURIComponent(revealedModal.host)}?token=${token}&size=large`}
                    alt="host"
                    draggable={false}
                    onContextMenu={(e) => e.preventDefault()}
                    className="max-w-full h-auto"
                    style={{
                      userSelect: "none",
                      pointerEvents: "none",
                      minHeight: "32px",
                      maxHeight: "120px",
                      imageRendering: "crisp-edges",
                    }}
                    data-testid="revealed-host-image-large"
                  />
                )}
              </div>

              {revealedModal?.note && (
                <div className="w-full text-center text-xs text-muted-foreground border-t border-border/30 pt-3">
                  Note : <span className="text-foreground">{revealedModal.note}</span>
                </div>
              )}

              <p className="text-[11px] text-muted-foreground/70 text-center italic max-w-xs">
                Copie desactivee pour des raisons de securite. Notez le host manuellement.
              </p>

              <Button
                onClick={() => setRevealedModal(null)}
                className="w-full"
                variant="default"
                data-testid="revealed-host-close-btn"
              >
                <X className="h-4 w-4 mr-2" />
                Fermer
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
};
const FirestickCodesTool = ({ isOpen, onClose, token, onUsageUpdate }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [categories, setCategories] = useState([]);
  // Browse mode removed - search only (anti-scraping)
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  // Fetch categories on mount
  useEffect(() => {
    if (isOpen) {
      fetchCategories();
    }
  }, [isOpen]);

  // Autocomplete effect
  useEffect(() => {
    const fetchAutocomplete = async () => {
      if (searchTerm.trim().length < 2) {
        setSuggestions([]);
        setShowSuggestions(false);
        return;
      }
      
      try {
        const response = await axios.get(`${API}/user/firestick-codes/autocomplete`, {
          params: { query: searchTerm, limit: 5 },
          headers: { Authorization: `Bearer ${token}` }
        });
        setSuggestions(response.data.suggestions || []);
        setShowSuggestions(response.data.suggestions?.length > 0);
      } catch (error) {
        console.error("Autocomplete error");
      }
    };

    const debounce = setTimeout(fetchAutocomplete, 300);
    return () => clearTimeout(debounce);
  }, [searchTerm, token]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/user/firestick-codes/categories`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCategories(response.data);
    } catch (error) {
      console.error("Error fetching categories");
    }
  };

  const handleSearch = async (query = null) => {
    const searchQuery = query || searchTerm;
    if (!searchQuery.trim()) {
      toast.error("Veuillez entrer un nom d'application ou un code");
      return;
    }

    setLoading(true);
    setResult(null);
    setShowSuggestions(false);

    try {
      const response = await axios.get(`${API}/user/tools/firestick-code`, {
        params: { app_name: searchQuery },
        headers: { Authorization: `Bearer ${token}` }
      });
      setResult(response.data);
      if (onUsageUpdate) onUsageUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la recherche");
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success("Code copié !");
    setTimeout(() => setCopied(false), 2000);
  };

  const handleClose = () => {
    setSearchTerm("");
    setResult(null);
    setSuggestions([]);
    setShowSuggestions(false);
    onClose();
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      setShowSuggestions(false);
      handleSearch();
    }
  };

  const handleSuggestionClick = (suggestion) => {
    setSearchTerm(suggestion.app_name);
    setShowSuggestions(false);
    handleSearch(suggestion.app_name);
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="firestick-tool-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Flame className="h-5 w-5 text-orange-500" />
            Firestick Codes
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Search Section */}
          <div className="space-y-2">
            <Label>Rechercher par nom d'application ou code</Label>
            <div className="relative">
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Input
                    placeholder="Ex: Kodi, TiviMate, 89893..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyPress={handleKeyPress}
                    onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
                    data-testid="firestick-search-input"
                  />
                  {/* Autocomplete Suggestions */}
                  {showSuggestions && suggestions.length > 0 && (
                    <div className="absolute z-50 w-full mt-1 bg-popover border border-border rounded-md shadow-lg max-h-48 overflow-y-auto">
                      {suggestions.map((sug, idx) => (
                        <div
                          key={idx}
                          className="px-3 py-2 hover:bg-accent cursor-pointer flex items-center justify-between"
                          onClick={() => handleSuggestionClick(sug)}
                        >
                          <span className="font-medium">{sug.app_name}</span>
                          <span className="text-sm text-muted-foreground font-mono">{sug.code}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <Button onClick={() => handleSearch()} disabled={loading} data-testid="firestick-search-btn">
                  {loading ? "..." : <Search className="h-4 w-4" />}
                </Button>
              </div>
            </div>
          </div>

          {/* Category filter tags - search only, no browse */}
          {categories.length > 0 && (
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs text-muted-foreground">Exemples :</span>
              {categories.slice(0, 4).map(cat => (
                <Button
                  key={cat}
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setSearchTerm(cat);
                    handleSearch(cat);
                  }}
                >
                  {cat}
                </Button>
              ))}
            </div>
          )}

          {/* Search Result */}
          {result && (
            <div className="space-y-3 pt-2" data-testid="firestick-result">
              {result.found ? (
                <Card className="bg-green-500/10 border-green-500/30">
                  <CardContent className="p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-semibold text-lg">{result.app_name}</p>
                        {result.category && (
                          <Badge variant="secondary" className="mt-1">{result.category}</Badge>
                        )}
                      </div>
                      <Check className="h-6 w-6 text-green-500" />
                    </div>
                    {result.description && (
                      <p className="text-sm text-muted-foreground">{result.description}</p>
                    )}
                    <div className="flex items-center gap-2 bg-background p-3 rounded-sm">
                      <span className="font-mono text-2xl font-bold text-primary flex-1">
                        {result.code}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleCopy(result.code)}
                        data-testid="copy-code-btn"
                      >
                        {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <Card className="bg-yellow-500/10 border-yellow-500/30">
                  <CardContent className="p-4 space-y-3">
                    <div className="flex items-center gap-2">
                      <AlertTriangle className="h-5 w-5 text-yellow-500" />
                      <span className="font-medium">Application non trouvée</span>
                    </div>
                    {result.suggestions && result.suggestions.length > 0 && (
                      <div className="space-y-2">
                        <p className="text-sm text-muted-foreground">Suggestions :</p>
                        <div className="flex flex-wrap gap-2">
                          {result.suggestions.map((sug, i) => (
                            <Button
                              key={i}
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setSearchTerm(sug);
                                handleSearch();
                              }}
                            >
                              {sug}
                            </Button>
                          ))}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {/* Search result only - no browse mode */}
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Domain Analysis Tool Component
const DomainAnalysisTool = ({ isOpen, onClose, token, onUsageUpdate }) => {
  const [domain, setDomain] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const handleAnalyze = async () => {
    if (!domain.trim()) {
      toast.error("Veuillez entrer un domaine ou une IP");
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      const response = await axios.post(
        `${API}/user/tools/domain-analysis`,
        { domain: domain.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setResult(response.data);
      if (onUsageUpdate) onUsageUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'analyse");
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleAnalyze();
    }
  };

  const getFlag = (countryCode) => {
    if (!countryCode || countryCode.length !== 2) return "🏳️";
    const codePoints = countryCode
      .toUpperCase()
      .split('')
      .map(char => 127397 + char.charCodeAt());
    return String.fromCodePoint(...codePoints);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-xl" data-testid="domain-analysis-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Globe className="h-5 w-5 text-primary" />
            Analyse Domaine
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Input */}
          <div className="space-y-2">
            <Label>Domaine ou IP à analyser</Label>
            <div className="flex gap-2">
              <Input
                value={domain}
                onChange={(e) => setDomain(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="exemple.com ou 192.168.1.1"
                data-testid="domain-input"
              />
              <Button 
                onClick={handleAnalyze} 
                disabled={loading}
                data-testid="analyze-btn"
              >
                {loading ? (
                  <span className="animate-spin">⏳</span>
                ) : (
                  <Search className="h-4 w-4" />
                )}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Accepte: domaine, IP ou URL complète
            </p>
          </div>

          {/* Results */}
          {result && (
            <div className="space-y-4 p-4 bg-muted/30 rounded-lg">
              {result.error ? (
                <div className="text-red-400 text-center py-4">
                  <AlertTriangle className="h-8 w-8 mx-auto mb-2" />
                  <p>{result.error}</p>
                </div>
              ) : (
                <>
                  {/* Status Bar */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className={`w-3 h-3 rounded-full ${result.is_up ? 'bg-green-500' : 'bg-red-500'}`} />
                      <span className={`font-medium ${result.is_up ? 'text-green-400' : 'text-red-400'}`}>
                        {result.is_up ? 'EN LIGNE' : 'HORS LIGNE'}
                      </span>
                    </div>
                    {result.ping_ms && (
                      <Badge variant="outline" className="font-mono">
                        {result.ping_ms} ms
                      </Badge>
                    )}
                  </div>

                  {/* Main Info Grid */}
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    {/* Domain */}
                    <div className="p-3 bg-background rounded-lg">
                      <p className="text-xs text-muted-foreground mb-1">Domaine</p>
                      <p className="font-mono font-medium truncate">{result.domain}</p>
                    </div>

                    {/* IP */}
                    <div className="p-3 bg-background rounded-lg">
                      <p className="text-xs text-muted-foreground mb-1">Adresse IP</p>
                      <p className="font-mono font-medium text-primary">{result.ip || '-'}</p>
                    </div>

                    {/* Country */}
                    <div className="p-3 bg-background rounded-lg">
                      <p className="text-xs text-muted-foreground mb-1">Pays</p>
                      <p className="font-medium">
                        {result.country_code && getFlag(result.country_code)} {result.country || '-'}
                      </p>
                    </div>

                    {/* ISP */}
                    <div className="p-3 bg-background rounded-lg">
                      <p className="text-xs text-muted-foreground mb-1">FAI / Hébergeur</p>
                      <p className="font-medium truncate">{result.isp || '-'}</p>
                    </div>
                  </div>

                  {/* Provider Match */}
                  <div className={`p-3 rounded-lg ${result.provider_name ? 'bg-green-500/10 border border-green-500/30' : 'bg-yellow-500/10 border border-yellow-500/30'}`}>
                    <p className="text-xs text-muted-foreground mb-1">Fournisseur correspondant</p>
                    {result.provider_name ? (
                      <div>
                        <p className="font-medium text-green-400 flex items-center gap-2">
                          <Server className="h-4 w-4" />
                          {result.provider_name}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          Trouvé par: {result.provider_match_type === 'domain' ? '📝 Nom de domaine' : '🔢 Adresse IP'}
                        </p>
                      </div>
                    ) : (
                      <p className="text-yellow-400">Aucun fournisseur trouvé (ni par nom, ni par IP)</p>
                    )}
                  </div>

                  {/* WHOIS Info */}
                  {(result.expiration_date || result.registrar || result.creation_date) && (
                    <div className="p-3 bg-background rounded-lg space-y-2">
                      <p className="text-xs text-muted-foreground font-medium">Informations WHOIS</p>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        {result.registrar && (
                          <div>
                            <p className="text-xs text-muted-foreground">Registrar</p>
                            <p className="truncate">{result.registrar}</p>
                          </div>
                        )}
                        {result.creation_date && (
                          <div>
                            <p className="text-xs text-muted-foreground">Créé le</p>
                            <p>{result.creation_date}</p>
                          </div>
                        )}
                        {result.expiration_date && (
                          <div className="col-span-2">
                            <p className="text-xs text-muted-foreground">Expiration</p>
                            <p className={`font-medium ${new Date(result.expiration_date) < new Date() ? 'text-red-400' : 'text-green-400'}`}>
                              {result.expiration_date}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

// M3U Analysis Tool Component
const M3UAnalysisTool = ({ isOpen, onClose, token, onUsageUpdate }) => {
  const [m3uUrl, setM3uUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const handleAnalyze = async () => {
    if (!m3uUrl.trim()) {
      toast.error("Veuillez entrer une URL M3U");
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      const response = await axios.post(
        `${API}/user/tools/m3u-analysis`,
        { m3u_url: m3uUrl.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setResult(response.data);
      if (onUsageUpdate) onUsageUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'analyse");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return "-";
    try {
      const date = new Date(parseInt(timestamp) * 1000);
      return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch {
      return timestamp;
    }
  };

  const isExpired = (expDate) => {
    if (!expDate) return false;
    try {
      const exp = new Date(parseInt(expDate) * 1000);
      return exp < new Date();
    } catch {
      return false;
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="m3u-analysis-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5 text-primary" />
            Analyse M3U
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 flex-1 overflow-auto">
          {/* Input */}
          <div className="space-y-2">
            <Label>URL M3U à analyser</Label>
            <Textarea
              value={m3uUrl}
              onChange={(e) => setM3uUrl(e.target.value)}
              placeholder="http://host.com:port/get.php?username=XXX&password=XXX&type=m3u_plus"
              className="font-mono text-sm min-h-[80px]"
              data-testid="m3u-url-input"
            />
            <Button 
              onClick={handleAnalyze} 
              disabled={loading}
              className="w-full"
              data-testid="analyze-m3u-btn"
            >
              {loading ? (
                <>
                  <span className="animate-spin mr-2">⏳</span>
                  Analyse en cours...
                </>
              ) : (
                <>
                  <Search className="h-4 w-4 mr-2" />
                  Analyser
                </>
              )}
            </Button>
          </div>

          {/* Results */}
          {result && (
            <div className="space-y-4">
              {result.error ? (
                <div className="text-red-400 text-center py-4 bg-red-500/10 rounded-lg">
                  <AlertTriangle className="h-8 w-8 mx-auto mb-2" />
                  <p>{result.error}</p>
                </div>
              ) : result.success ? (
                <>
                  {/* Server Info */}
                  <div className="p-4 bg-muted/30 rounded-lg">
                    <h3 className="font-medium mb-3 flex items-center gap-2">
                      <Server className="h-4 w-4" />
                      Serveur
                    </h3>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <p className="text-xs text-muted-foreground">Host</p>
                        <p className="font-mono">{result.server?.host || result.host}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Port</p>
                        <p className="font-mono">{result.server?.port || result.port}</p>
                      </div>
                      {result.server?.https_port && (
                        <div>
                          <p className="text-xs text-muted-foreground">Port HTTPS</p>
                          <p className="font-mono">{result.server.https_port}</p>
                        </div>
                      )}
                      {result.server?.timezone && (
                        <div>
                          <p className="text-xs text-muted-foreground">Timezone</p>
                          <p>{result.server.timezone}</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Account Info */}
                  {result.account && (
                    <div className="p-4 bg-muted/30 rounded-lg">
                      <h3 className="font-medium mb-3 flex items-center gap-2">
                        <User className="h-4 w-4" />
                        Compte
                      </h3>
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div>
                          <p className="text-xs text-muted-foreground">Username</p>
                          <p className="font-mono">{result.account.username}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Statut</p>
                          <Badge variant={result.account.status === "Active" ? "default" : "destructive"}>
                            {result.account.status}
                          </Badge>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Connexions</p>
                          <p>{result.account.active_cons} / {result.account.max_connections}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Trial</p>
                          <p>{result.account.is_trial ? "Oui" : "Non"}</p>
                        </div>
                        {result.account.exp_date && (
                          <div className="col-span-2">
                            <p className="text-xs text-muted-foreground">Expiration</p>
                            <p className={`font-medium ${isExpired(result.account.exp_date) ? 'text-red-400' : 'text-green-400'}`}>
                              {formatDate(result.account.exp_date)}
                              {isExpired(result.account.exp_date) && " (EXPIRÉ)"}
                            </p>
                          </div>
                        )}
                        {result.account.created_at && (
                          <div className="col-span-2">
                            <p className="text-xs text-muted-foreground">Créé le</p>
                            <p>{formatDate(result.account.created_at)}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Stats */}
                  {result.stats && (
                    <div className="p-4 bg-muted/30 rounded-lg">
                      <h3 className="font-medium mb-3 flex items-center gap-2">
                        <BarChart3 className="h-4 w-4" />
                        Statistiques
                      </h3>
                      <div className="grid grid-cols-3 gap-4">
                        {/* Live */}
                        <div className="text-center p-3 bg-blue-500/10 rounded-lg">
                          <Tv className="h-6 w-6 mx-auto mb-2 text-blue-400" />
                          <p className="text-2xl font-bold text-blue-400">{result.stats.live_streams}</p>
                          <p className="text-xs text-muted-foreground">Chaînes Live</p>
                          <p className="text-xs text-muted-foreground">({result.stats.live_categories} catégories)</p>
                        </div>
                        {/* VOD */}
                        <div className="text-center p-3 bg-purple-500/10 rounded-lg">
                          <Film className="h-6 w-6 mx-auto mb-2 text-purple-400" />
                          <p className="text-2xl font-bold text-purple-400">{result.stats.vod_streams}</p>
                          <p className="text-xs text-muted-foreground">Films VOD</p>
                          <p className="text-xs text-muted-foreground">({result.stats.vod_categories} catégories)</p>
                        </div>
                        {/* Series */}
                        <div className="text-center p-3 bg-green-500/10 rounded-lg">
                          <PlayCircle className="h-6 w-6 mx-auto mb-2 text-green-400" />
                          <p className="text-2xl font-bold text-green-400">{result.stats.series}</p>
                          <p className="text-xs text-muted-foreground">Séries</p>
                          <p className="text-xs text-muted-foreground">({result.stats.series_categories} catégories)</p>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-yellow-400 text-center py-4 bg-yellow-500/10 rounded-lg">
                  <AlertTriangle className="h-8 w-8 mx-auto mb-2" />
                  <p>Analyse incomplète. Vérifiez l'URL.</p>
                </div>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Find Panel Link Tool Component
const FindPanelTool = ({ isOpen, onClose, token }) => {
  const [panelLinks, setPanelLinks] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const [selected, setSelected] = useState(null);
  const [showDropdown, setShowDropdown] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchPanelLinks();
      setSelected(null);
      setSearch("");
    }
  }, [isOpen]);

  const fetchPanelLinks = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/user/panel-links`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPanelLinks(res.data);
    } catch {
      toast.error("Erreur chargement des liens panel");
    } finally {
      setLoading(false);
    }
  };

  const suggestions = search.length > 0
    ? panelLinks.filter((p) => p.name.toLowerCase().includes(search.toLowerCase()))
    : [];

  const handleSelect = (provider) => {
    setSelected(provider);
    setSearch(provider.name);
    setShowDropdown(false);
  };

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setSelected(null);
    setShowDropdown(true);
  };

  const handleCopy = (url) => {
    navigator.clipboard.writeText(url);
    setCopiedId(selected.id);
    toast.success("Lien copie !");
    setTimeout(() => setCopiedId(null), 2000);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="find-panel-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Globe className="h-5 w-5 text-primary" />
            Trouver lien panel
          </DialogTitle>
        </DialogHeader>

        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Tapez le nom du fournisseur..."
            value={search}
            onChange={handleSearchChange}
            onFocus={() => search.length > 0 && setShowDropdown(true)}
            className="pl-10"
            autoFocus
            data-testid="panel-search-input"
          />

          {showDropdown && suggestions.length > 0 && !selected && (
            <div className="absolute z-50 w-full mt-1 max-h-[200px] overflow-y-auto rounded-md border border-border bg-popover shadow-lg" data-testid="panel-suggestions">
              {suggestions.map((p) => (
                <button
                  key={p.id}
                  onClick={() => handleSelect(p)}
                  className="w-full text-left px-4 py-2.5 text-sm hover:bg-muted/50 transition-colors border-b border-border/30 last:border-0"
                  data-testid={`suggestion-${p.id}`}
                >
                  {p.name}
                </button>
              ))}
            </div>
          )}

          {showDropdown && search.length > 0 && suggestions.length === 0 && !selected && (
            <div className="absolute z-50 w-full mt-1 rounded-md border border-border bg-popover shadow-lg px-4 py-3 text-sm text-muted-foreground">
              Aucun fournisseur trouve
            </div>
          )}
        </div>

        {selected && (
          <div className="mt-2 p-4 rounded-lg border border-primary/30 bg-primary/5 space-y-3" data-testid="panel-result">
            <p className="font-medium text-sm">{selected.name}</p>
            <p className="text-xs font-mono text-muted-foreground break-all select-all">{selected.panel_url}</p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                className="flex-1"
                onClick={() => handleCopy(selected.panel_url)}
                data-testid="copy-panel-link"
              >
                {copiedId === selected.id ? (
                  <><Check className="h-3.5 w-3.5 mr-2 text-emerald-400" /> Copie !</>
                ) : (
                  <><Copy className="h-3.5 w-3.5 mr-2" /> Copier le lien</>
                )}
              </Button>
              <a
                href={selected.panel_url.startsWith("http") ? selected.panel_url : `https://${selected.panel_url}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1"
                data-testid="open-panel-link"
              >
                <Button variant="default" size="sm" className="w-full">
                  <ArrowRight className="h-3.5 w-3.5 mr-2" /> Ouvrir
                </Button>
              </a>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

// Script M3U Client Tool Component
const ScriptM3uTool = ({ isOpen, onClose, token }) => {
  const [m3uLink, setM3uLink] = useState("");
  const [generatedHtml, setGeneratedHtml] = useState("");
  const [copied, setCopied] = useState(false);
  const [template, setTemplate] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (isOpen && token) {
      axios.get(`${API}/user/tools/script-m3u-template`, {
        headers: { Authorization: `Bearer ${token}` }
      }).then(res => setTemplate(res.data.template)).catch(() => {});
    }
  }, [isOpen, token]);

  const parseM3uLink = (link) => {
    try {
      const url = new URL(link.trim());
      const username = url.searchParams.get("username") || "";
      const password = url.searchParams.get("password") || "";
      const serverUrl = `${url.protocol}//${url.host}`;
      return { username, password, serverUrl, fullUrl: link.trim() };
    } catch {
      return null;
    }
  };

  const validateM3u = (link) => {
    if (!link.trim()) return false;
    const lower = link.toLowerCase();
    return (lower.includes("get.php") || lower.includes("m3u") || lower.includes("username=")) && lower.startsWith("http");
  };

  const htmlToPlainText = (html) => {
    const div = document.createElement("div");
    // Remove everything in <head>, <style>, <script> before extracting text
    const cleaned = html.replace(/<head[\s\S]*?<\/head>/gi, "")
                        .replace(/<style[\s\S]*?<\/style>/gi, "")
                        .replace(/<script[\s\S]*?<\/script>/gi, "")
                        .replace(/<!DOCTYPE[^>]*>/gi, "")
                        .replace(/<html[^>]*>/gi, "")
                        .replace(/<\/html>/gi, "")
                        .replace(/<body[^>]*>/gi, "")
                        .replace(/<\/body>/gi, "");
    div.innerHTML = cleaned;
    return (div.innerText || div.textContent || "").trim();
  };

  const handleGenerate = () => {
    setError("");
    if (!validateM3u(m3uLink)) {
      setError("Format invalide. Le lien doit être un lien M3U valide (ex: http://...get.php?username=...&password=...&type=m3u_plus)");
      return;
    }
    const parsed = parseM3uLink(m3uLink);
    if (!parsed || !parsed.username) {
      setError("Impossible d'extraire le username/password du lien.");
      return;
    }
    let html = template || "";
    html = html.replace(/\{\{FULL_URL\}\}/g, parsed.fullUrl);
    html = html.replace(/\{\{USERNAME\}\}/g, parsed.username);
    html = html.replace(/\{\{PASSWORD\}\}/g, parsed.password);
    html = html.replace(/\{\{SERVER_URL\}\}/g, parsed.serverUrl);
    setGeneratedHtml(html);
    // Log usage
    axios.post(`${API}/user/tools/log`, {
      tool_name: "Script M3U Client",
      input_data: m3uLink,
      output_data: `Username: ${parsed.username}, Password: ${parsed.password}, Server: ${parsed.serverUrl}`
    }, { headers: { Authorization: `Bearer ${token}` } }).catch(() => {});
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(htmlToPlainText(generatedHtml));
    setCopied(true);
    toast.success("Texte copié !");
    setTimeout(() => setCopied(false), 2000);
  };

  const handleClose = () => {
    setM3uLink("");
    setGeneratedHtml("");
    setError("");
    setCopied(false);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="script-m3u-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-primary" />
            Script M3U Client
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Lien M3U du client</Label>
            <div className="flex gap-2">
              <Input
                placeholder="http://serveur.com/get.php?username=xxx&password=xxx&type=m3u_plus"
                value={m3uLink}
                onChange={(e) => setM3uLink(e.target.value)}
                className="flex-1"
                data-testid="script-m3u-input"
              />
              <Button onClick={handleGenerate} data-testid="script-m3u-generate">
                Générer
              </Button>
            </div>
            {error && <p className="text-xs text-destructive">{error}</p>}
          </div>

          {generatedHtml && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-primary">Script généré</Label>
                <Button variant="outline" size="sm" onClick={handleCopy} data-testid="script-m3u-copy">
                  {copied ? <Check className="h-4 w-4 mr-1 text-green-500" /> : <Copy className="h-4 w-4 mr-1" />}
                  {copied ? "Copié" : "Copier"}
                </Button>
              </div>
              <div className="border border-border rounded-sm overflow-hidden">
                <div className="bg-white p-4 max-h-[400px] overflow-y-auto" dangerouslySetInnerHTML={{ __html: generatedHtml }} />
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Active Atlas -> M3U Tool Component
const ActiveAtlasTool = ({ isOpen, onClose, token }) => {
  return (
    <AtlasGenericTool
      isOpen={isOpen}
      onClose={onClose}
      token={token}
      title="Active Atlas → M3U"
      toolName="Active Atlas -> M3u"
      configEndpoint="/user/tools/atlas-config"
      defaultConfig={{ server: "", path: "/get.php", type: "m3u_plus", output: "ts", password: "" }}
      testIdPrefix="atlas"
    />
  );
};

// Atlas Code -> M3U Tool Component
const AtlasCodeTool = ({ isOpen, onClose, token }) => {
  return (
    <AtlasGenericTool
      isOpen={isOpen}
      onClose={onClose}
      token={token}
      title="Atlas Code → M3U"
      toolName="Atlas Code -> M3u"
      configEndpoint="/user/tools/atlas-code-config"
      defaultConfig={{ server: "", path: "/get.php", type: "m3u_plus", output: "ts", password: "" }}
      testIdPrefix="atlas-code"
    />
  );
};

// Shared Atlas-style tool: takes a numeric code and builds an M3U link from server config
const AtlasGenericTool = ({ isOpen, onClose, token, title, toolName, configEndpoint, defaultConfig, testIdPrefix }) => {
  const [code, setCode] = useState("");
  const [result, setResult] = useState("");
  const [copied, setCopied] = useState(false);
  const [config, setConfig] = useState(defaultConfig);

  useEffect(() => {
    if (isOpen && token) {
      axios.get(`${API}${configEndpoint}`, {
        headers: { Authorization: `Bearer ${token}` }
      }).then(res => setConfig(res.data)).catch(() => {});
    }
  }, [isOpen, token, configEndpoint]);

  const buildLink = (value) => {
    if (!value || !config.server) return "";
    const pwd = config.password || value;
    return `http://${config.server}${config.path}?username=${value}&password=${pwd}&type=${config.type}&output=${config.output}`;
  };

  const handleCodeChange = (e) => {
    const value = e.target.value.replace(/[^0-9]/g, "").slice(0, 20);
    setCode(value);
    setResult(value ? buildLink(value) : "");
  };

  useEffect(() => {
    if (code) setResult(buildLink(code));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config]);

  const handleCopy = () => {
    if (!result) return;
    navigator.clipboard.writeText(result);
    setCopied(true);
    toast.success("Lien M3U copié !");
    setTimeout(() => setCopied(false), 2000);
    axios.post(`${API}/user/tools/log`, {
      tool_name: toolName,
      input_data: `Code: ${code}`,
      output_data: result
    }, { headers: { Authorization: `Bearer ${token}` } }).catch(() => {});
  };

  const handleClose = () => {
    setCode("");
    setResult("");
    setCopied(false);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg" data-testid={`${testIdPrefix}-tool-dialog`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Map className="h-5 w-5 text-primary" />
            {title}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Code (chiffres uniquement, max 20)</Label>
            <Input
              placeholder="Ex: 23312233122"
              value={code}
              onChange={handleCodeChange}
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={20}
              data-testid={`${testIdPrefix}-code-input`}
            />
            <p className="text-xs text-muted-foreground">Entrez votre code numérique pour générer votre lien M3U</p>
          </div>

          {result && (
            <div className="space-y-2" data-testid={`${testIdPrefix}-result`}>
              <Label className="text-primary">Lien M3U généré</Label>
              <div className="flex items-start gap-2 bg-muted/30 border border-border rounded-sm p-3">
                <code className="text-xs text-primary break-all flex-1 select-all">{result}</code>
                <Button
                  variant="outline"
                  size="icon"
                  className="shrink-0 h-8 w-8"
                  onClick={handleCopy}
                  data-testid={`${testIdPrefix}-copy-btn`}
                >
                  {copied ? <Check className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

const UserDashboard = () => {
  const [tools, setTools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [m3uToolOpen, setM3uToolOpen] = useState(false);
  const [findHostToolOpen, setFindHostToolOpen] = useState(false);
  const [firestickToolOpen, setFirestickToolOpen] = useState(false);
  const [domainAnalysisToolOpen, setDomainAnalysisToolOpen] = useState(false);
  const [m3uAnalysisToolOpen, setM3uAnalysisToolOpen] = useState(false);
  const [panelToolOpen, setPanelToolOpen] = useState(false);
  const [atlasToolOpen, setAtlasToolOpen] = useState(false);
  const [atlasCodeToolOpen, setAtlasCodeToolOpen] = useState(false);
  const [scriptM3uToolOpen, setScriptM3uToolOpen] = useState(false);
  const [dnsToolOpen, setDnsToolOpen] = useState(false);
  const [dnsV2ToolOpen, setDnsV2ToolOpen] = useState(false);
  const [dnsV3ToolOpen, setDnsV3ToolOpen] = useState(false);
  const [dnsV4ToolOpen, setDnsV4ToolOpen] = useState(false);
  const [platformStats, setPlatformStats] = useState(null);
  const { user, token, logout } = useAuth();
  const [currentUser, setCurrentUser] = useState(user);
  const navigate = useNavigate();

  const fetchTools = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/user/tools`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTools(response.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error(error.response.data.detail);
      } else {
        toast.error("Erreur lors du chargement des outils");
      }
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchUserData = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCurrentUser(response.data);
    } catch (error) {
      console.error("Error fetching user data");
    }
  }, [token]);

  const fetchPlatformStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/user/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPlatformStats(response.data);
    } catch (error) {
      console.error("Error fetching platform stats");
    }
  }, [token]);

  useEffect(() => {
    fetchTools();
    fetchUserData();
    fetchPlatformStats();
  }, [fetchTools, fetchUserData, fetchPlatformStats]);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const handleToolClick = (tool) => {
    if (tool.name === "M3u -> Xstream") {
      setM3uToolOpen(true);
    } else if (tool.name === "Trouver votre host") {
      setFindHostToolOpen(true);
    } else if (tool.name === "Firestick Codes") {
      setFirestickToolOpen(true);
    } else if (tool.name === "Analyse Domaine") {
      setDomainAnalysisToolOpen(true);
    } else if (tool.name === "Analyse M3U") {
      setM3uAnalysisToolOpen(true);
    } else if (tool.name === "Lien Panel") {
      setPanelToolOpen(true);
    } else if (tool.name === "Active Atlas -> M3u") {
      setAtlasToolOpen(true);
    } else if (tool.name === "Atlas Code -> M3u") {
      setAtlasCodeToolOpen(true);
    } else if (tool.name === "Script M3U Client") {
      setScriptM3uToolOpen(true);
    } else if (tool.name === "DNS Host Checker") {
      setDnsToolOpen(true);
    } else if (tool.name === "Check DNS V2") {
      setDnsV2ToolOpen(true);
    } else if (tool.name === "Check DNS V3") {
      setDnsV3ToolOpen(true);
    } else if (tool.name === "DNS Checker V4" || tool.name === "Check DNS V4") {
      setDnsV4ToolOpen(true);
    } else {
      // For other tools, just show a message
      toast.info(`Outil "${tool.name}" - Fonctionnalité à venir`);
    }
  };

  const handleUsageUpdate = () => {
    fetchUserData();
  };

  const usagePercent = currentUser ? (currentUser.daily_usage / currentUser.daily_limit) * 100 : 0;
  const isExpired = currentUser?.expiration_date && new Date(currentUser.expiration_date) < new Date();

  return (
    <div className="min-h-screen bg-background" data-testid="user-dashboard">
      {/* Header */}
      <header className="border-b border-border bg-card sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-sm">
              <Wrench className="h-6 w-6 text-primary" />
            </div>
            <div>
              <h1 className="text-xl font-bold tracking-tight">Mes Outils</h1>
              <p className="text-sm text-muted-foreground">
                Bienvenue, {currentUser?.first_name || currentUser?.login}
                <span className="ml-2 text-[10px] font-mono text-muted-foreground/60" data-testid="app-version-header">
                  {APP_VERSION}
                </span>
              </p>
            </div>
          </div>
          <Button 
            variant="ghost" 
            onClick={handleLogout}
            className="text-muted-foreground hover:text-destructive"
            data-testid="user-logout"
          >
            <LogOut className="h-5 w-5 mr-2" />
            Déconnexion
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Account Status Alert */}
        {isExpired && (
          <div className="mb-6 p-4 bg-destructive/10 border border-destructive/30 rounded-sm flex items-center gap-3">
            <AlertTriangle className="h-5 w-5 text-destructive" />
            <div>
              <p className="font-medium text-destructive">Compte expiré</p>
              <p className="text-sm text-muted-foreground">Contactez l'administrateur pour renouveler votre accès.</p>
            </div>
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <Card className="bg-card border-border">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <p className="text-muted-foreground text-xs uppercase tracking-wider">Utilisation Quotidienne</p>
                <Activity className="h-4 w-4 text-primary" />
              </div>
              <div className="flex items-end gap-2 mb-2">
                <span className="text-3xl font-bold font-mono text-primary">{currentUser?.daily_usage || 0}</span>
                <span className="text-muted-foreground font-mono mb-1">/ {currentUser?.daily_limit || 100}</span>
              </div>
              <Progress value={usagePercent} className="h-2" />
            </CardContent>
          </Card>

          <Card className="bg-card border-border">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <p className="text-muted-foreground text-xs uppercase tracking-wider">Expiration</p>
                <CalendarDays className="h-4 w-4 text-accent" />
              </div>
              <p className="text-2xl font-bold font-mono">
                {currentUser?.expiration_date ? (
                  <span className={isExpired ? "text-destructive" : "text-foreground"}>
                    {format(new Date(currentUser.expiration_date), "dd MMM yyyy", { locale: fr })}
                  </span>
                ) : (
                  <span className="text-primary">Illimité</span>
                )}
              </p>
            </CardContent>
          </Card>

          <Card className="bg-card border-border">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <p className="text-muted-foreground text-xs uppercase tracking-wider">Dernière Connexion</p>
                <Clock className="h-4 w-4 text-muted-foreground" />
              </div>
              <p className="text-xl font-mono text-muted-foreground">
                {currentUser?.last_access 
                  ? format(new Date(currentUser.last_access), "dd/MM/yy HH:mm")
                  : "-"
                }
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Platform Stats */}
        {platformStats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8" data-testid="platform-stats">
            <div className="flex items-center gap-3 p-3 rounded-lg border border-border bg-card">
              <Globe className="h-5 w-5 text-blue-400 shrink-0" />
              <div>
                <p className="text-2xl font-bold font-mono text-blue-400">{platformStats.total_providers}</p>
                <p className="text-xs text-muted-foreground">Fournisseurs</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 rounded-lg border border-border bg-card">
              <Server className="h-5 w-5 text-cyan-400 shrink-0" />
              <div>
                <p className="text-2xl font-bold font-mono text-cyan-400">{platformStats.total_hosts}</p>
                <p className="text-xs text-muted-foreground">Hosts</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 rounded-lg border border-border bg-card">
              <Download className="h-5 w-5 text-violet-400 shrink-0" />
              <div>
                <p className="text-2xl font-bold font-mono text-violet-400">{platformStats.total_firestick}</p>
                <p className="text-xs text-muted-foreground">Codes Downloader</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 rounded-lg border border-border bg-card">
              <Wrench className="h-5 w-5 text-amber-400 shrink-0" />
              <div>
                <p className="text-2xl font-bold font-mono text-amber-400">{platformStats.total_tools}</p>
                <p className="text-xs text-muted-foreground">Outils Actifs</p>
              </div>
            </div>
          </div>
        )}

        {/* Tools Section */}
        <h2 className="text-2xl font-bold tracking-tight mb-4">Outils Disponibles</h2>
        
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3].map((i) => (
              <Card key={i} className="bg-card border-border animate-pulse">
                <CardContent className="p-6">
                  <div className="h-12 w-12 bg-muted rounded-sm mb-4" />
                  <div className="h-6 bg-muted rounded w-2/3 mb-2" />
                  <div className="h-4 bg-muted rounded w-full" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : tools.length === 0 ? (
          <Card className="bg-card border-border">
            <CardContent className="p-8 text-center">
              <Wrench className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-medium mb-2">Aucun outil disponible</h3>
              <p className="text-muted-foreground">
                Contactez l'administrateur pour obtenir l'accès aux outils.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {tools.map((tool) => {
              const IconComponent = getToolIcon(tool.icon);
              const isM3uTool = tool.name === "M3u -> Xstream";
              return (
                <Card 
                  key={tool.id} 
                  className="bg-card border-border hover:border-primary/30 transition-all group"
                  data-testid={`tool-card-${tool.id}`}
                >
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between">
                      <div className="w-[50px] h-[50px] rounded-sm overflow-hidden flex items-center justify-center bg-primary/10 group-hover:bg-primary/20 transition-colors">
                        {tool.icon_url ? (
                          <img src={tool.icon_url} alt={tool.name} className="w-full h-full object-cover" />
                        ) : (
                          <IconComponent className="h-6 w-6 text-primary" />
                        )}
                      </div>
                      <Badge variant="secondary" className="font-mono text-xs">
                        {tool.enabled ? "Actif" : "Inactif"}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <CardTitle className="text-lg mb-2">{tool.name}</CardTitle>
                    <p className="text-sm text-muted-foreground mb-4">
                      {tool.description || "Outil de conversion"}
                    </p>
                    <Button 
                      className={`w-full ${(tool.name === "M3u -> Xstream" || tool.name === "Trouver votre host" || tool.name === "Firestick Codes" || tool.name === "Analyse Domaine" || tool.name === "Analyse M3U" || tool.name === "Lien Panel" || tool.name === "Active Atlas -> M3u" || tool.name === "Atlas Code -> M3u" || tool.name === "Script M3U Client") ? 'bg-primary text-primary-foreground hover:bg-primary/90' : 'bg-secondary text-secondary-foreground hover:bg-secondary/80'}`}
                      onClick={() => handleToolClick(tool)}
                      disabled={isExpired || usagePercent >= 100}
                      data-testid={`use-tool-${tool.id}`}
                    >
                      <Play className="h-4 w-4 mr-2" />
                      {(tool.name === "M3u -> Xstream" || tool.name === "Trouver votre host" || tool.name === "Firestick Codes" || tool.name === "Analyse Domaine" || tool.name === "Analyse M3U" || tool.name === "Lien Panel" || tool.name === "Active Atlas -> M3u" || tool.name === "Atlas Code -> M3u" || tool.name === "Script M3U Client" || tool.name === "DNS Host Checker" || tool.name === "Check DNS V2" || tool.name === "Check DNS V3" || tool.name === "DNS Checker V4" || tool.name === "Check DNS V4") ? "Ouvrir" : "Bientôt"}
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </main>

      {/* M3u to Xtream Tool Dialog */}
      <M3uToXtreamTool 
        isOpen={m3uToolOpen} 
        onClose={() => setM3uToolOpen(false)} 
        token={token}
        onUsageUpdate={handleUsageUpdate}
      />

      {/* Find Host Tool Dialog */}
      <FindHostTool 
        isOpen={findHostToolOpen} 
        onClose={() => setFindHostToolOpen(false)} 
        token={token}
        onUsageUpdate={handleUsageUpdate}
      />

      {/* Firestick Codes Tool Dialog */}
      <FirestickCodesTool 
        isOpen={firestickToolOpen} 
        onClose={() => setFirestickToolOpen(false)} 
        token={token}
        onUsageUpdate={handleUsageUpdate}
      />

      {/* Domain Analysis Tool Dialog */}
      <DomainAnalysisTool 
        isOpen={domainAnalysisToolOpen} 
        onClose={() => setDomainAnalysisToolOpen(false)} 
        token={token}
        onUsageUpdate={handleUsageUpdate}
      />

      {/* M3U Analysis Tool Dialog */}
      <M3UAnalysisTool 
        isOpen={m3uAnalysisToolOpen} 
        onClose={() => setM3uAnalysisToolOpen(false)} 
        token={token}
        onUsageUpdate={handleUsageUpdate}
      />

      {/* Find Panel Link Tool Dialog */}
      <FindPanelTool 
        isOpen={panelToolOpen} 
        onClose={() => setPanelToolOpen(false)} 
        token={token}
      />

      {/* Active Atlas -> M3U Tool Dialog */}
      <ActiveAtlasTool 
        isOpen={atlasToolOpen} 
        onClose={() => setAtlasToolOpen(false)} 
        token={token}
      />

      {/* Atlas Code -> M3U Tool Dialog */}
      <AtlasCodeTool 
        isOpen={atlasCodeToolOpen} 
        onClose={() => setAtlasCodeToolOpen(false)} 
        token={token}
      />

      {/* Script M3U Client Tool Dialog */}
      <ScriptM3uTool 
        isOpen={scriptM3uToolOpen} 
        onClose={() => setScriptM3uToolOpen(false)} 
        token={token}
      />

      {/* DNS Host Checker Dialog */}
      <Dialog open={dnsToolOpen} onOpenChange={setDnsToolOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="dns-tool-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Globe className="h-5 w-5 text-primary" />
              DNS Host Checker
            </DialogTitle>
          </DialogHeader>
          <DnsHostChecker token={token} />
        </DialogContent>
      </Dialog>

      {/* Check DNS V2 Dialog */}
      <Dialog open={dnsV2ToolOpen} onOpenChange={setDnsV2ToolOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="dns-v2-tool-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Globe className="h-5 w-5 text-primary" />
              Check DNS V2
            </DialogTitle>
          </DialogHeader>
          <DnsCheckerV2 token={token} />
        </DialogContent>
      </Dialog>

      {/* Check DNS V3 Dialog */}
      <Dialog open={dnsV3ToolOpen} onOpenChange={setDnsV3ToolOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="dns-v3-tool-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Globe className="h-5 w-5 text-primary" />
              Check DNS V3
            </DialogTitle>
          </DialogHeader>
          <DnsCheckerV3 token={token} />
        </DialogContent>
      </Dialog>

      {/* DNS Checker V4 Dialog */}
      <Dialog open={dnsV4ToolOpen} onOpenChange={setDnsV4ToolOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="dns-v4-tool-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Globe className="h-5 w-5 text-primary" />
              DNS Checker V4
            </DialogTitle>
          </DialogHeader>
          <DnsCheckerV4 token={token} />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UserDashboard;
